import { ArrowRightIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Ellipsis, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { useState } from 'react';
import styled from 'styled-components';

import { CATEGORY_COLOR } from './constants';
import { EnvironmentDetailPanel } from './EnvironmentDetailPanel';
import { CompactCardPadding, Dot, EnvironmentRow, NoteBox, SurfaceCard } from './styled';
import { EnvironmentUsageRow, SapUsageDashboardData } from './types';
import { UsageBar } from './UsageBar';
import { formatCredits } from './utils';

const EnvironmentName = styled.div`
  flex: none;
  width: 190px;
  text-align: left;
`;

const EnvironmentCredits = styled.div`
  flex: none;
  width: 90px;
  text-align: right;
`;

function EnvironmentBar({
  environment,
  maxCredits,
  onSelect
}: {
  environment: EnvironmentUsageRow;
  maxCredits: number;
  onSelect: () => void;
}) {
  return (
    <EnvironmentRow type='button' onClick={onSelect} data-test-id={`sap-usage-environment-row-${environment.id}`}>
      <EnvironmentName>
        <Ellipsis tooltip>
          <Text level='xs' tone={theme.textColor.secondary}>
            {environment.name} ({environment.tenantId})
          </Text>
        </Ellipsis>
      </EnvironmentName>
      <StackItem grow='1'>
        <UsageBar
          height={theme.sizes.x16}
          borderRadius={theme.radius.x4}
          total={maxCredits}
          segments={[{ value: environment.dataCredits, color: CATEGORY_COLOR.data }]}
        />
      </StackItem>
      <EnvironmentCredits>
        <Text level='xs' tone={theme.textColor.secondary}>
          {formatCredits(environment.dataCredits)} CU
        </Text>
      </EnvironmentCredits>
      <ArrowRightIcon type='default' size='s' color={theme.colors.gray500} />
    </EnvironmentRow>
  );
}

export function UsageByEnvironment({ data }: { data: SapUsageDashboardData }) {
  const [selectedId, setSelectedId] = useState<string>();

  const { environments, environmentsSummary } = data;
  const maxCredits = Math.max(...environments.map((env) => env.dataCredits), 1);
  const selected = environments.find((env) => env.id === selectedId);

  return (
    <SurfaceCard data-test-id='sap-usage-by-environment'>
      <CompactCardPadding gap={theme.spaces.x16}>
        <Stack gap={theme.spaces.x6}>
          <Text level='sm' weight={theme.fontWeight.medium}>
            Usage by environment
          </Text>
          <Text level='xs' tone={theme.textColor.tertiary}>
            Data charges per data plane · click a row for detail
          </Text>
        </Stack>

        <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
          <Dot $color={CATEGORY_COLOR.data} />
          <Text level='xs' tone={theme.textColor.tertiary}>
            Data
          </Text>
        </Stack>

        <Stack gap={theme.spaces.x2}>
          {environments.map((environment) => (
            <EnvironmentBar
              key={environment.id}
              environment={environment}
              maxCredits={maxCredits}
              onSelect={() => setSelectedId(environment.id)}
            />
          ))}
        </Stack>

        <NoteBox>
          <Text level='xs' tone={theme.textColor.tertiary}>
            Total capacity consumed is{' '}
            <Text level='xs' weight={theme.fontWeight.semiBold} elementType='span'>
              {formatCredits(environmentsSummary.totalCredits)} CU
            </Text>
            . Environments account for{' '}
            <Text level='xs' weight={theme.fontWeight.semiBold} elementType='span'>
              {formatCredits(environmentsSummary.environmentCredits)} CU
            </Text>
            . The remaining{' '}
            <Text level='xs' weight={theme.fontWeight.semiBold} elementType='span'>
              {formatCredits(environmentsSummary.accountLevelCredits)} CU is billed at account level
            </Text>
          </Text>
        </NoteBox>
      </CompactCardPadding>

      {selected && <EnvironmentDetailPanel environment={selected} onClose={() => setSelectedId(undefined)} />}
    </SurfaceCard>
  );
}
